import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, RefreshCw, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";

export default function TvMatch() {
  const { lang } = useLang();
  const de = lang === "de";
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const importList = async () => {
    if (!text.trim()) { toast.error(de ? "Bitte die TeamViewer-Liste einfügen" : "Please paste the TeamViewer list"); return; }
    setBusy("import");
    try {
      const res = await api.post("/teamviewer/import-list", { text });
      setResult(res.data);
      toast.success(`${res.data.parsed} ${de ? "Einträge gelesen" : "entries parsed"} · ${res.data.matched} ${de ? "IDs neu zugeordnet" : "IDs matched"}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const liveSync = async () => {
    setBusy("sync");
    try {
      const res = await api.post("/teamviewer/sync");
      setResult(res.data);
      toast.success(`${de ? "Live-Sync" : "Live sync"}: ${res.data.matched} ${de ? "neu, " : "new, "}${res.data.updated} ${de ? "aktualisiert" : "updated"}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="w-full px-4 sm:px-6 py-6 space-y-5">
        <div className="fade-up">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <MonitorSmartphone className="w-6 h-6 text-[#c00000]" /> {de ? "TeamViewer-ID-Abgleich" : "TeamViewer ID matching"}
          </h1>
          <p className="text-sm text-gray-600 mt-2 max-w-3xl">
            {de
              ? "Füge hier entweder die vollständige Geräteliste aus der TeamViewer-Konsole ODER eine einfache zweispaltige Liste „Geräte-Code  TeamViewer-ID“ ein. Das System ordnet jeder Station anhand des Geräte-Codes automatisch ihre ID zu."
              : "Paste either the full device list from the TeamViewer console OR a simple two-column list \"device code  TeamViewer ID\". The system assigns each station its ID by device code."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="tv-live-sync"
            onClick={liveSync}
            disabled={busy === "sync"}
            className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 transition-colors"
          >
            {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {de ? "Live-API-Sync" : "Live API sync"}
          </button>
        </div>

        <div className="card-surface rounded-xl p-5 space-y-3">
          <label className="text-xs font-mono uppercase tracking-wider text-gray-600">
            {de ? "TeamViewer-Geräteliste (Rohtext einfügen)" : "TeamViewer device list (paste raw text)"}
          </label>
          <textarea
            data-testid="tv-paste"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            placeholder="remotecontrol_id ... alias ... online_state ..."
            className="w-full h-[300px] bg-white border border-gray-200 rounded-xl p-4 text-xs font-mono text-gray-800 leading-relaxed focus:border-[#c00000] focus:ring-1 focus:ring-[#c00000] outline-none resize-y"
          />
          <button
            data-testid="tv-import"
            onClick={importList}
            disabled={busy === "import"}
            className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-white font-semibold rounded-lg px-5 py-2.5 text-sm disabled:opacity-50 transition-colors"
          >
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {de ? "Importieren & abgleichen" : "Import & match"}
          </button>
        </div>

        {result && (
          <div className="card-surface rounded-xl p-5 grid grid-cols-3 gap-4" data-testid="tv-result">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "Gelesen" : "Parsed"}</div>
              <div className="font-display text-3xl font-bold text-gray-900">{result.parsed ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "IDs neu zugeordnet" : "IDs matched"}</div>
              <div className="font-display text-3xl font-bold text-[#c00000]">{result.matched}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "Status aktualisiert" : "Status updated"}</div>
              <div className="font-display text-3xl font-bold text-[#c00000]">{result.updated}</div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
